import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  icon?: LucideIcon;
  /** Show the back-to-dashboard chevron. Default: true on mobile, hidden on lg+ where sidebar replaces it. */
  showBack?: boolean;
  backTo?: string;
  /** Right-side actions (buttons, badges, etc). */
  actions?: ReactNode;
  className?: string;
}

export default function PageHeader({
  title,
  subtitle,
  icon: Icon,
  showBack = false,
  backTo = "/dashboard",
  actions,
  className,
}: PageHeaderProps) {
  const navigate = useNavigate();

  return (
    <header
      className={cn(
        "relative flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between",
        "pl-12 lg:pl-0", // leave room for mobile sidebar toggle
        className,
      )}
    >
      <div className="flex items-start gap-3 min-w-0">
        {showBack && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(backTo)}
            className="shrink-0 text-muted-foreground hover:text-foreground"
            aria-label="Back to dashboard"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
        )}
        {Icon && (
          <div className="hidden sm:flex shrink-0 mt-0.5 h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary/25 to-secondary/15 border border-primary/30 text-primary shadow-[0_0_24px_hsl(var(--primary)/0.15)]">
            <Icon className="h-5 w-5" />
          </div>
        )}
        <div className="min-w-0">
          <h1 className="font-heading font-extrabold text-2xl sm:text-3xl text-foreground leading-tight">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
              {subtitle}
            </p>
          )}
        </div>
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </header>
  );
}